import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LoaderCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { AddReferralDrawer } from "@/components/AddReferralDrawer";
import { defaultTabForRole, tabKeysForRole } from "@/components/AppShell";
import { AdminOperations, RewardRules } from "@/components/AdminViews";
import { AdminApplications } from "@/components/AdminApplications";
import { AdminMembers } from "@/components/AdminMembers";
import { AdminOverview } from "@/components/AdminOverview";
import { AdminStaff } from "@/components/AdminStaff";
import { AdminSolarSites } from "@/components/AdminSolarSites";
import { MemberOverview, ReferralList, RewardsView } from "@/components/MemberViews";
import { AmbassadorAdditions, TradePartnerAdditions } from "@/components/RoleAdditions";
import { MonitoringSection } from "@/sections/monitoring";
import { SupportSection } from "@/sections/support/SupportSection";
import { api, roleLabel } from "@/lib/api";

const fetchMemberBundle = async () => {
  const [member, stats, tier, referrals, rewards, summary] = await Promise.all([
    api.get("/members/me"),
    api.get("/members/me/stats"),
    api.get("/members/me/tier"),
    api.get("/referrals"),
    api.get("/rewards"),
    api.get("/rewards/summary"),
  ]);
  return {
    member: member.data,
    stats: stats.data,
    tier: tier.data,
    referrals: referrals.data.items,
    rewards: rewards.data,
    summary: summary.data,
  };
};

const fetchAdminBundle = async () => {
  const [overview, pending, fraud, activeRule, versions] = await Promise.all([
    api.get("/admin/overview"),
    api.get("/admin/rewards/pending"),
    api.get("/admin/fraud"),
    api.get("/admin/reward-rules/active"),
    api.get("/admin/reward-rules"),
  ]);
  return {
    overview: overview.data,
    pending: pending.data,
    fraud: fraud.data,
    activeRule: activeRule.data,
    versions: versions.data,
  };
};

export const LoadingView = () => (
  <div className="screen-state premium-card">
    <LoaderCircle className="spin" />
    <h2>Preparing your Alliance workspace</h2>
    <p>Syncing your secure member and reward data…</p>
  </div>
);

export const ErrorView = ({ error, retry }) => (
  <div className="screen-state premium-card">
    <h2>We couldn’t load this workspace</h2>
    <p>{error.response?.data?.detail || error.message}</p>
    <Button onClick={retry}><RefreshCw />Try again</Button>
  </div>
);

const pageTitle = (activeTab) => {
  if (activeTab === "overview") return "Program overview";
  if (activeTab === "applications") return "Membership applications";
  if (activeTab === "members") return "Member directory";
  if (activeTab === "rules") return "Reward governance";
  if (activeTab === "staff") return "Staff & access";
  return "Operations queue";
};

const memberTitle = (activeTab, member) => {
  if (activeTab === "home") return `Good morning, ${member.name.split(" ")[0]}`;
  if (activeTab === "refer") return "Your referrals";
  if (activeTab === "support") return "Solar support";
  return "Your rewards";
};

const memberSubtitle = (activeTab) => {
  if (activeTab === "home") return "Here’s how your solar network is growing.";
  if (activeTab === "refer") return "Transparent stages, synced from Greentek ERP.";
  return "Every calculation preserved by rule version.";
};

const AdminWorkspace = ({ role, activeTab, data, reload }) => {
  if (activeTab === "solar-sites") return <AdminSolarSites />;
  // Monitoring brings its own header/queries (admin-gated via ADMIN_TABS).
  if (activeTab === "monitoring") return <MonitoringSection role={role} />;
  if (activeTab === "support") return <SupportSection />;

  const approve = async (id) => {
    try {
      const { data: result } = await api.post(`/admin/rewards/${id}/approve`);
      toast.success(
        result.frappe_write === "gated"
          ? "Approved · ERP invoice write is safely gated"
          : "Approved and draft invoice created",
      );
      reload();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        detail === "supplier_link_required"
          ? "Link this member to an ERP Supplier first"
          : detail || "Approval failed",
      );
    }
  };

  const clearFraud = async (id) => {
    try {
      await api.post(`/admin/fraud/${id}/clear`);
      toast.success("Fraud flag cleared and audit entry saved");
      reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not clear flag");
    }
  };

  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">GREENTEK INDIA · ALLIANCE CONTROL</p>
          <h1>{pageTitle(activeTab)}</h1>
          <span>Commercial truth from Frappe. Attribution and rewards from Alliance.</span>
        </div>
        <span className="live-pill"><i /> Live</span>
      </div>
      {activeTab === "overview" && <AdminOverview overview={data.overview} pendingRewards={data.pending} fraud={data.fraud} />}
      {activeTab === "applications" && <AdminApplications />}
      {activeTab === "members" && <AdminMembers />}
      {activeTab === "rules" && <RewardRules activeRule={data.activeRule} versions={data.versions} reload={reload} />}
      {activeTab === "operations" && <AdminOperations pending={data.pending} fraud={data.fraud} approve={approve} clearFraud={clearFraud} reload={reload} />}
      {activeTab === "staff" && <AdminStaff />}
    </>
  );
};

const MemberWorkspace = ({ role, activeTab, data, drawerOpen, setDrawerOpen, reload }) => {
  // Monitoring brings its own header and queries, the same as the admin workspace.
  if (activeTab === "monitoring") return <MonitoringSection role={role} />;
  if (activeTab === "support") return <SupportSection />;

  return (
    <>
      <div className="page-heading member-heading">
        <div>
          <p className="eyebrow">{roleLabel(role).toUpperCase()}</p>
          <h1>{memberTitle(activeTab, data.member)}</h1>
          <span>{memberSubtitle(activeTab)}</span>
        </div>
      </div>
      {activeTab === "home" && <MemberOverview member={data.member} stats={data.stats} referrals={data.referrals} onAddReferral={() => setDrawerOpen(true)} />}
      {activeTab === "home" && ["ambassador", "trade_partner"].includes(role) && <AmbassadorAdditions stats={data.stats} member={data.member} />}
      {activeTab === "home" && role === "trade_partner" && <TradePartnerAdditions stats={data.stats} />}
      {activeTab === "refer" && <ReferralList referrals={data.referrals} onAddReferral={() => setDrawerOpen(true)} />}
      {activeTab === "rewards" && <RewardsView summary={data.summary} rewards={data.rewards} />}
      <AddReferralDrawer open={drawerOpen} setOpen={setDrawerOpen} memberId={data.member.id} role={role} onCreated={reload} />
    </>
  );
};

export const RoleRouter = ({ role, memberId, accessReady }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pathTab = useLocation().pathname.replace(/^\//, "");
  const activeTab = tabKeysForRole(role).includes(pathTab)
    ? pathTab
    : defaultTabForRole(role);
  const memberBundleRequired =
    role !== "admin" && ["home", "refer", "rewards"].includes(activeTab);
  const memberQuery = useQuery({
    queryKey: ["member-bundle", role, memberId],
    queryFn: fetchMemberBundle,
    enabled: accessReady && memberBundleRequired,
  });
  const adminQuery = useQuery({
    queryKey: ["admin-bundle", role],
    queryFn: fetchAdminBundle,
    enabled: accessReady && role === "admin",
  });

  if (!accessReady) return <LoadingView />;
  if (role === "admin") {
    if (adminQuery.isLoading) return <LoadingView />;
    if (adminQuery.error) {
      return <ErrorView error={adminQuery.error} retry={adminQuery.refetch} />;
    }
    return (
      <AdminWorkspace
        role={role}
        activeTab={activeTab}
        data={adminQuery.data}
        reload={adminQuery.refetch}
      />
    );
  }
  if (memberBundleRequired && memberQuery.isLoading) return <LoadingView />;
  if (memberBundleRequired && memberQuery.error) {
    return <ErrorView error={memberQuery.error} retry={memberQuery.refetch} />;
  }
  return (
    <MemberWorkspace
      role={role}
      activeTab={activeTab}
      data={memberQuery.data}
      drawerOpen={drawerOpen}
      setDrawerOpen={setDrawerOpen}
      reload={memberQuery.refetch}
    />
  );
};
